import { Component, OnInit, OnDestroy, NgZone, ChangeDetectorRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AfterViewInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { AppLinksCfgService, AppLink } from '../common/appLinks/appLinksCfg.service';
import { AccessManagerService } from '../auth/accessManager/accessManager.service';
import { LoadManagerService } from '../common/loadingOverlay/loadManager.service';
import { DashboardCfgService } from '../dashboard/dashboardCfg.service';
import { INamedClass } from '../common/iNamedClass';
import { LoggerService } from '../common/logging/logger.service';
import { AppLinksProfessionalsCfgService } from '../common/appLinks/appLinksProfessionalsCfg.service';
import { FeaturePermissionsService } from '../auth/raas/featurePermissions.service';
import { OfficeSelectorService } from '../common/officeSelector/officeSelector.service';
import { AccessManagerResourceActionType } from '../auth/accessManager/accessManager.models';
import { PowerBiDialogComponent } from '../common/launcher/powerbi/components/powerbi-dialog/powerbi-dialog.component';
import { ApigeeService } from '../auth/apigee/apigee.service';

@Component({
    selector: 'home-classic',
    templateUrl: './homeClassic.component.html',
    styleUrls: ['./homeClassic.component.css'],
    providers: [LoadManagerService],
    standalone: false
})
export class HomeClassicComponent implements OnInit, OnDestroy, INamedClass, AfterViewInit {
    public getClassName() { return "HomeClassicComponent"; }

    private mBusy: boolean;
    private mLoadStatusUpdatedSubscription: Subscription;
    private mDashboardAccessGranted: Promise<boolean>;
    private mIsProfessionals: boolean = false;
    private mOfficeSelectionChangedSubscription: Subscription;
    public showOfficeSelector: boolean = false;

    private mAppLinks: AppLink[];
    public get appLinks(): AppLink[] {
        return this.mAppLinks;
    }

    public get isProfessionals(): boolean {
        return this.mIsProfessionals;
    }

    public get isFundation(): boolean {
        return this.officeSelectorService.selectedOffice && this.officeSelectorService.selectedOffice.isFundation;
    }

    @ViewChild(PowerBiDialogComponent) private powerBiDialogSelector: PowerBiDialogComponent;

    constructor(private zone: NgZone, private router: Router,
        private logger: LoggerService,
        private apigeeService: ApigeeService, private accessManagerService: AccessManagerService,
        private appLinksCfgService: AppLinksCfgService, private appLinksProfessionalsCfgService: AppLinksProfessionalsCfgService,
        private loadManagerService: LoadManagerService,
        private changeDetectorRef: ChangeDetectorRef,
        private dashboardCfgService: DashboardCfgService,
        private featurePermissionsService: FeaturePermissionsService,
        private officeSelectorService: OfficeSelectorService) {
        this.mAppLinks = [];
    }

    public get busy(): boolean {
        return this.mBusy;
    }

    public async ngOnInit(): Promise<void> {
        this.showOfficeSelector = false;
        sessionStorage.removeItem("appAttemps");

        this.mLoadStatusUpdatedSubscription = this.loadManagerService.loadStatusUpdated.subscribe((value) => {
            this.zone.run(() => {
                this.mBusy = value;
                this.changeDetectorRef.detectChanges();
            });
        });

        this.mOfficeSelectionChangedSubscription = this.officeSelectorService.selectedOfficeUpdated.subscribe(() => {
            if (this.officeSelectorService.selectedOffice && this.officeSelectorService.selectedOffice.isTEA) {
                this.router.navigate(['dashboard']);
            }
        });

        this.loadManagerService.incLoadRequest();
    }

    public ngOnDestroy(): void {
        if (this.mLoadStatusUpdatedSubscription) this.mLoadStatusUpdatedSubscription.unsubscribe();
        if (this.mOfficeSelectionChangedSubscription) this.mOfficeSelectionChangedSubscription.unsubscribe();
    }

    public async ngAfterViewInit(): Promise<void> {
        try {
            this.mDashboardAccessGranted = new Promise<boolean>(async (resolve, reject) => {
                try {
                    let apigee = this.apigeeService;

                    if (apigee.apiToken == null && (apigee.googleAuthToken == null || apigee.googleDirectoryToken == null)) {
                        throw new Error("No credentials present");
                    }
                    else {
                        await this.featurePermissionsService.professionalsEnabled().then(result => this.zone.run(() => this.mIsProfessionals = result));

                        let permissions = await this.accessManagerService.getResourcePermissions(this.dashboardCfgService.getCfg().resourceId);
                        if (permissions && permissions.getResourceAction(AccessManagerResourceActionType.ApplicationAccess)) {
                            resolve(true);
                        } else {
                            resolve(false);
                        }
                    }
                } catch (error) {
                    reject(error);
                }
            });

            if (!await this.mDashboardAccessGranted) {
                this.logger.traceActivity("Acceso a HomeClassic");
                await this.loadAppCards();
            } else {
                if (this.mIsProfessionals || this.isFundation) {
                    if (this.officeSelectorService.selectedOffice && this.officeSelectorService.selectedOffice.isTEA) {
                        this.router.navigate(['dashboard']);
                    } else {
                        this.logger.traceActivity("Acceso a HomeClassic");
                        this.showOfficeSelector = true;
                        await this.loadAppCards();
                    }
                } else {
                    this.router.navigate(['dashboard']);
                }
            }
        } catch (error) {
            this.zone.run(() => this.router.navigate(['login']));
        }
        finally {
            this.loadManagerService.decLoadRequest();
        }
    }

    private async loadAppCards(): Promise<void> {
        try {
            this.loadManagerService.incLoadRequest();

            let appLinks = await this.featurePermissionsService.professionalsEnabled() && !this.isFundation ? this.appLinksProfessionalsCfgService.getCfg().appLinks : this.appLinksCfgService.getCfg().appLinks;
            appLinks = appLinks.sort((a, b) => a.order - b.order);

            this.mAppLinks = [];
            for (let i = 0; i < appLinks.length; ++i) {
                let permissions = await this.accessManagerService.getResourcePermissions(appLinks[i].resourceId);
                if (permissions && permissions.getResourceAction(AccessManagerResourceActionType.WindowAccess)) {
                    this.mAppLinks.push(appLinks[i]);
                }
            }
        } finally {
            this.loadManagerService.decLoadRequest();
        }
    }

    powerBiDialog() {
        this.powerBiDialogSelector.openDialog('FBI Informes');
    }
}
